/**
 * The hours-decision notification (PX-W6.4, spec I-3 / decision D-2): the
 * durable record of a manager's Confirm / Send back, delivered through the
 * STANDARD notification create + the existing websocket bus — no client-local
 * notification store, no second channel, no polling.
 *
 * Wire contract (verified against `MongoNotification` + the
 * `NotificationApplicationService.gateNotificationContext` seam, 2026-09-04):
 *  - `scope` is REQUIRED on the parse seam (`notification.scope.required`) —
 *    `"user"` is the discriminator of a per-user row carrying `targetUserId`.
 *  - `severity`, `title`, `description`, `source`, `contextType`,
 *    `contextId`, `eventType` are all required (`@NotNull`/`@NotBlank`).
 *  - `status`, `slug`, timestamps are server-enriched — never sent.
 *  - A row whose context names no project (`contextType` ≠ `"project"`, no
 *    `projectId`) needs the caller to be an ORG ADMIN — which the decision
 *    verbs already require (`HoursBusinessService.assertMutable` admits only
 *    the author-of-a-draft or an org-admin), so the gate and the decision
 *    coincide by construction.
 *
 * The bus then emits `notification.created` (already in W6.1's
 * `EVENT_TYPE_MAP` → the notifications slice's row upsert) — this ticket adds
 * no bus row.
 */

import { apiClient } from '@/services'

import type { HoursEntry } from '../types'

/** The event type of an hours decision row — pinned by the contract suite. */
export const HOURS_DECISION_EVENT_TYPE = 'hours.decision'

/** The context vocabulary of the decision row. */
export const HOURS_DECISION_CONTEXT_TYPE = 'hours-entry'

/** Where the submitter lands when they follow the notification. */
export const HOURS_DECISION_CONTEXT_HREF = '/my/timesheet'

/** The `source` stamp naming the surface that raised the row. */
export const HOURS_DECISION_SOURCE = 'PX-W6.4'

/** The already-translated title/description, composed in the actor's locale. */
export interface DecisionNotificationCopy {
  title: string
  description: string
}

/**
 * Record exactly ONE decision notification, addressed to the entry's author.
 *
 * @param entry - The submitted entry the decision was made on.
 * @param copy - The translated title/description (the caller owns i18n — the
 *   decision's own wording is part of the copy, not of this seam).
 */
export async function recordDecisionNotification(
  entry: HoursEntry,
  copy: DecisionNotificationCopy
): Promise<void> {
  await apiClient.post('/{orgId}/notifications', {
    scope: 'user',
    targetUserId: entry.userId,
    severity: 'info',
    title: copy.title,
    description: copy.description,
    source: HOURS_DECISION_SOURCE,
    contextType: HOURS_DECISION_CONTEXT_TYPE,
    contextId: entry.id,
    contextHref: HOURS_DECISION_CONTEXT_HREF,
    eventType: HOURS_DECISION_EVENT_TYPE
  })
}
